import { useState, useEffect } from "react"

/**
 * Canvas dimensions interface
 */
export interface CanvasDimensions {
  width: number
  height: number
}

/**
 * Return type for useCanvasDimensions hook
 */
export interface CanvasDimensionsResult {
  canvasDimensions: CanvasDimensions
  scaleX: number
  scaleY: number
}

/**
 * Custom hook for managing canvas dimensions and coordinate scaling.
 *
 * Observes the canvas element's dimensions and provides scaling factors
 * to convert between abstract PowerPoint units and actual pixel coordinates.
 *
 * @param canvasRef - Reference to the canvas DOM element
 * @param POWERPOINT_WIDTH_UNITS - Abstract width units for the canvas
 * @param POWERPOINT_HEIGHT_UNITS - Abstract height units for the canvas
 *
 * @returns Canvas dimension data and scaling factors
 *
 * @example
 * const canvasRef = useRef<HTMLDivElement>(null)
 * const { canvasDimensions, scaleX, scaleY } = useCanvasDimensions(canvasRef, 1440, 810)
 *
 * // Convert abstract units to pixels
 * const pixelX = abstractX * scaleX
 * const pixelY = abstractY * scaleY
 */
export const useCanvasDimensions = (
  canvasRef: React.RefObject<HTMLDivElement | null>,
  POWERPOINT_WIDTH_UNITS: number,
  POWERPOINT_HEIGHT_UNITS: number
): CanvasDimensionsResult => {
  const [canvasDimensions, setCanvasDimensions] = useState<CanvasDimensions>({
    width: 0,
    height: 0,
  })

  useEffect(() => {
    const target = canvasRef.current
    if (!target) return

    const resizeObserver = new ResizeObserver(
      (entries: ResizeObserverEntry[]) => {
        for (const entry of entries) {
          const { width, height } = entry.contentRect
          setCanvasDimensions((prev) =>
            prev.width !== width || prev.height !== height
              ? { width, height }
              : prev
          )
        }
      }
    )

    resizeObserver.observe(target)
    const { width, height } = target.getBoundingClientRect()
    setCanvasDimensions({ width, height })

    return () => resizeObserver.disconnect()
  }, [])

  const scaleX = canvasDimensions.width
    ? canvasDimensions.width / POWERPOINT_WIDTH_UNITS
    : 1
  const scaleY = canvasDimensions.height
    ? canvasDimensions.height / POWERPOINT_HEIGHT_UNITS
    : 1

  return { canvasDimensions, scaleX, scaleY }
}
